import type { OrganizationRole } from '@/types/auth';

/**
 * Client mirror of pos-api's capability matrix (V1-ROLES / blocker B8).
 *
 * ⚠ UX ONLY. `pos-api/app/Http/Middleware/PosRoleMiddleware.php` +
 * `app/Enums/OrganizationRole.php` are authoritative; this module exists so the
 * console does not present controls the server will reject with 403
 * (07-roles-permissions.md §4: adapt a verified role "for UX while retaining
 * server checks"). Never treat a `true` here as permission — the request still
 * has to pass the server.
 *
 * Keep the capability names and the ladder identical to the server enum. When
 * the role is unknown/absent (pre-roles backend), every capability is treated
 * as ALLOWED so the console behaves exactly as it did before roles existed and
 * the server remains the only gate.
 */
export type Capability =
  | 'sell'
  | 'orders.edit'
  | 'orders.edit.final'
  | 'orders.returns'
  | 'orders.refund'
  | 'orders.discount'
  | 'tables.serve'
  | 'tables.fire'
  | 'shift.open'
  | 'shift.close'
  | 'catalog.manage'
  | 'purchasing.view'
  | 'purchasing.manage'
  | 'purchasing.approve'
  | 'inventory.receive'
  | 'inventory.count'
  | 'inventory.transfer'
  | 'inventory.adjust'
  | 'inventory.cost.view'
  | 'inventory.cost.edit'
  | 'inventory.exception.approve'
  | 'inventory.receipt.reverse'
  | 'reviews.manage'
  | 'customers.create'
  | 'customers.manage'
  | 'branches.manage'
  | 'tables.manage'
  | 'terminals.view'
  | 'terminals.manage'
  | 'office'
  | 'staff.manage'
  | 'apps.manage'
  | 'themes.manage';

const RANK: Record<OrganizationRole, number> = {
  owner: 40,
  manager: 30,
  cashier: 20,
  waiter: 10,
};

/** capability → minimum role. Mirrors OrganizationRole::capabilityMatrix(). */
const MINIMUM: Record<Capability, OrganizationRole> = {
  sell: 'cashier',
  // Order editing (V2.3). Three capabilities, because they answer three
  // different questions, and the UI needs all three to decide what to show:
  //
  //   orders.edit        is the Edit control offered at all
  //   orders.edit.final  may this person save a delivered/settled/cancelled/
  //                      refunded order, or do they see the manager gate
  //   orders.discount    is the discount field editable inside the editor
  //
  // Only the first is a route gate server-side. The other two are decided
  // against the LOCKED order upstream, so a `true` here is even less of a
  // permission than usual — it means "offer the control and let the server
  // answer", never "this will succeed".
  'orders.edit': 'cashier',
  'orders.edit.final': 'manager',
  'orders.returns': 'manager',
  'orders.refund': 'manager',
  'orders.discount': 'manager',
  // Restaurant fast-follow. Table service — read the floor, open a tab, append
  // a round, flag the bill — is the one thing a waiter does, so it has its own
  // capability instead of lowering `sell`. Settling a tab is money and stays
  // on `sell`, which is why a waiter-only account sees the floor but not the
  // settle control.
  'tables.serve': 'waiter',
  // docs/07-roles-permissions.md §1: "Queue advance — assigned table/order
  // only" and "Sell/tender — no; sends open tickets". A waiter must be able to
  // push their table's tab to the kitchen. The server route resolves a TAB
  // first, so this is not general queue advance.
  'tables.fire': 'waiter',
  'shift.open': 'cashier',
  'shift.close': 'manager',
  'catalog.manage': 'manager',
  'purchasing.view': 'manager',
  'purchasing.manage': 'manager',
  'purchasing.approve': 'manager',
  'inventory.receive': 'manager',
  'inventory.count': 'manager',
  'inventory.transfer': 'manager',
  'inventory.adjust': 'manager',
  'inventory.cost.view': 'manager',
  'inventory.cost.edit': 'manager',
  'inventory.exception.approve': 'manager',
  'inventory.receipt.reverse': 'owner',
  'reviews.manage': 'manager',
  'customers.create': 'cashier',
  'customers.manage': 'manager',
  'branches.manage': 'manager',
  // The floor plan itself — which tables exist, what they are called, whether
  // they are still in service. Structural setup, so it sits with
  // `branches.manage` at manager rather than with the waiter-level
  // `tables.serve` above: serving a table and deciding the floor has one are
  // different authorities.
  'tables.manage': 'manager',
  // A waiter cannot finish /setup without reading their own terminals, and
  // table service is unreachable without a workstation. The route returns
  // only the caller's assigned terminals.
  'terminals.view': 'waiter',
  // Standing at a till is every role; deciding which tills exist and who may
  // stand at one is branch/terminal config.
  'terminals.manage': 'manager',
  office: 'manager',
  // Appointing members hands out every other grant, so it sits above the rest
  // of the office at owner.
  'staff.manage': 'owner',
  'apps.manage': 'owner',
  // Storefront theme source. Owner only, on reads as well as writes, matching
  // OrganizationRole::capabilityMatrix() — theme source is the code the shop is
  // served from, so reading it exposes the whole storefront and downloading it
  // exports it.
  'themes.manage': 'owner',
};

/**
 * What the SERVER said this caller may do in the organization currently in
 * scope, from `GET {pos}/me` (pos-api Api\POS\MeController).
 *
 * `granted` is the resolved list for the signed-in role. `known` is every
 * capability name that backend recognises — the two are separate because a name
 * absent from `granted` alone is ambiguous: it means "this role may not" OR
 * "this backend is older than this name", which demand opposite handling. With
 * `known`, can() applies the server's answer for names the server knows and
 * falls back to the table below for anything newer, so a console ahead of its
 * API degrades to the previous behaviour instead of hiding working controls.
 *
 * Both are null until a reconcile lands, and are CLEARED whenever the
 * organization in scope changes — a grant is per-membership, and carrying one
 * tenant's permissions into another is worse than having none.
 */
let serverGranted: ReadonlySet<string> | null = null;
let serverKnown: ReadonlySet<string> | null = null;

/** Called by AuthStore only: after a reconcile, and with (null, null) to clear. */
export function setServerCapabilities(
  granted: readonly string[] | null,
  known: readonly string[] | null,
): void {
  // A vocabulary is what makes the grants interpretable, so they arrive
  // together or not at all. An empty vocabulary would silently mean "trust the
  // local table for everything", which is the safe reading, but treating it as
  // "no server opinion" says so explicitly.
  if (!granted || !known || known.length === 0) {
    serverGranted = null;
    serverKnown = null;
    return;
  }
  serverGranted = new Set(granted);
  serverKnown = new Set(known);
}

/** Test/diagnostic reader: which capabilities the server actually reported. */
export function serverCapabilities(): { granted: string[]; known: string[] } | null {
  if (!serverGranted || !serverKnown) return null;
  return { granted: [...serverGranted], known: [...serverKnown] };
}

/**
 * True when a role may attempt a capability.
 *
 * Order of authority:
 *  1. the server's own answer for this membership, when it has given one for
 *     this capability name;
 *  2. the rank table below, which is a MIRROR of
 *     OrganizationRole::capabilityMatrix() and can drift from it;
 *  3. `null` role (backend did not report one) → true: assume nothing and let
 *     the server decide, which is the pre-roles behaviour.
 *
 * Still UX only. pos-api authorizes every request regardless; this decides what
 * to put on screen, never what is allowed.
 */
export function can(role: OrganizationRole | null | undefined, capability: Capability): boolean {
  if (serverKnown?.has(capability)) return serverGranted!.has(capability);
  if (!role) return true;
  return RANK[role] >= RANK[MINIMUM[capability]];
}

/** Convenience for the office route group, which is entirely manager+. */
export function canUseOffice(role: OrganizationRole | null | undefined): boolean {
  return can(role, 'office');
}
